import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShoppingCart, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

// Product recommendation rules based on Ajax Systems categories
const RECOMMENDATION_RULES = {
  // Centrales (Hubs) → recommander détecteurs, claviers, sirènes
  hubs: {
    title: 'Complétez votre système',
    subtitle: 'Détecteurs et accessoires compatibles',
    recommendCategories: ['detectors', 'keypads', 'sirens', 'accessories'],
    recommendSubcategories: ['motion', 'door', 'smoke', 'keypad', 'siren']
  },
  // Détecteurs de mouvement → recommander autres détecteurs + centrale si pas
  detectors: {
    title: 'Produits complémentaires',
    subtitle: 'Renforcez votre protection',
    recommendCategories: ['hubs', 'detectors', 'sirens'],
    recommendSubcategories: ['door', 'smoke', 'glass', 'siren']
  },
  // Détecteurs d'ouverture → recommander détecteurs mouvement + centrale
  door: {
    title: 'Protection complète',
    subtitle: 'Détecteurs de mouvement et sirènes',
    recommendCategories: ['hubs', 'detectors', 'sirens'],
    recommendSubcategories: ['motion', 'siren']
  },
  // Caméras → recommander NVR + autres caméras
  cameras: {
    title: 'Vidéosurveillance',
    subtitle: 'Enregistreurs et caméras supplémentaires',
    recommendCategories: ['nvr', 'cameras'],
    recommendSubcategories: ['nvr', 'camera_outdoor', 'camera_indoor']
  },
  // NVR → recommander caméras
  nvr: {
    title: 'Caméras compatibles',
    subtitle: 'Étendez votre couverture vidéo',
    recommendCategories: ['cameras'],
    recommendSubcategories: ['camera_outdoor', 'camera_indoor', 'camera_ptz']
  },
  // Sirènes → recommander détecteurs + claviers
  sirens: {
    title: 'Système complet',
    subtitle: 'Détecteurs et contrôle',
    recommendCategories: ['detectors', 'keypads'],
    recommendSubcategories: ['motion', 'door', 'keypad']
  },
  // Domotique → recommander autres produits domotique
  automation: {
    title: 'Smart Home Ajax',
    subtitle: 'Automatisez votre maison',
    recommendCategories: ['automation'],
    recommendSubcategories: ['relay', 'socket', 'lightswitch']
  },
  // SSI / Incendie → recommander détecteurs fumée + sirènes
  fire: {
    title: 'Sécurité incendie',
    subtitle: 'Détecteurs et alertes',
    recommendCategories: ['fire', 'sirens'],
    recommendSubcategories: ['smoke', 'heat', 'co', 'siren']
  },
  // Par défaut
  default: {
    title: 'Produits populaires',
    subtitle: 'Découvrez notre sélection',
    recommendCategories: ['basic', 'hubs', 'detectors'],
    recommendSubcategories: []
  }
};

const ProductRecommendations = ({ currentProduct, category, currentProductId, excludeProductIds = [], maxItems = 4, compact = false }) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    if (currentProduct || category) {
      fetchRecommendations();
    }
  }, [currentProduct, category, excludeProductIds.length]);

  const fetchRecommendations = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/products`);
      if (response.ok) {
        const allProducts = await response.json();
        const filtered = getRecommendedProducts(allProducts);
        setRecommendations(filtered.slice(0, maxItems));
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRecommendedProducts = (allProducts) => {
    // Use currentProduct if available, otherwise build from category
    const current = currentProduct || { category, product_id: currentProductId };
    
    // Build list of products to exclude (current + already in cart)
    const excludeIds = new Set([
      current.product_id,
      currentProductId,
      ...excludeProductIds
    ].filter(Boolean));
    
    // Determine which rule to apply based on current product
    let rule = RECOMMENDATION_RULES.default;
    
    // Check subcategory first for more specific rules
    if (current.subcategory && RECOMMENDATION_RULES[current.subcategory]) {
      rule = RECOMMENDATION_RULES[current.subcategory];
    } else if (current.category && RECOMMENDATION_RULES[current.category]) {
      rule = RECOMMENDATION_RULES[current.category];
    }
    
    // Filter products based on rules
    const recommended = allProducts.filter(product => {
      // Don't recommend products already in cart or current product
      if (excludeIds.has(product.product_id)) return false;
      
      // Don't recommend products without price (sur devis)
      if (!product.price) return false;
      
      // Check if category matches
      const categoryMatch = rule.recommendCategories.includes(product.category) ||
                           rule.recommendCategories.includes(product.subcategory);
      
      // Check if subcategory matches (if specified)
      const subcategoryMatch = rule.recommendSubcategories.length === 0 ||
                              rule.recommendSubcategories.includes(product.subcategory);
      
      return categoryMatch || subcategoryMatch;
    });
    
    // Sort by popularity (badge first) and shuffle a bit for variety
    return recommended.sort((a, b) => {
      if (a.badge && !b.badge) return -1;
      if (!a.badge && b.badge) return 1;
      return Math.random() - 0.5;
    });
  };

  const getRuleInfo = () => {
    const current = currentProduct || { category };
    if (!current) return RECOMMENDATION_RULES.default;
    
    if (current.subcategory && RECOMMENDATION_RULES[current.subcategory]) {
      return RECOMMENDATION_RULES[current.subcategory];
    }
    if (current.category && RECOMMENDATION_RULES[current.category]) {
      return RECOMMENDATION_RULES[current.category];
    }
    return RECOMMENDATION_RULES.default;
  };

  const handleAddToCart = (product) => {
    addToCart(product, 1, 'black');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
      </div>
    );
  }

  if (recommendations.length === 0) {
    return null;
  }

  const ruleInfo = getRuleInfo();

  // Get the appropriate category link for "See all"
  const getShopLink = () => {
    const current = currentProduct || { category };
    if (!current) return '/shop';
    
    // Map product categories to shop filters
    const categoryMap = {
      'basic': '/shop?category=basic',
      'hubs': '/shop?subcategory=hubs',
      'detectors': '/shop?subcategory=detectors',
      'cameras': '/shop?category=video_basic',
      'nvr': '/shop?category=video_basic',
      'sirens': '/shop?subcategory=sirens',
      'keypads': '/shop?subcategory=keypads',
      'automation': '/shop?category=automation',
      'fire': '/shop?category=fire'
    };
    
    return categoryMap[current.subcategory] || 
           categoryMap[current.category] || 
           '/shop';
  };

  // Compact mode for checkout
  if (compact) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="product-recommendations-compact">
        {recommendations.map(product => (
          <div 
            key={product.product_id}
            className="bg-black/40 border border-white/5 rounded-lg overflow-hidden hover:border-fgs-primary/50 transition-colors group"
          >
            <Link to={`/product/${product.product_id}`} className="block">
              <div className="relative aspect-square bg-gradient-to-br from-fgs-surface to-fgs-bg p-2">
                <img
                  src={product.image_url || product.images?.black}
                  alt={product.name}
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                  loading="lazy"
                />
                {product.discount_percent && (
                  <span className="absolute top-1 right-1 px-1.5 py-0.5 bg-red-500 rounded text-[10px] text-white">
                    -{product.discount_percent}%
                  </span>
                )}
              </div>
            </Link>
            <div className="p-2">
              <Link to={`/product/${product.product_id}`}>
                <h3 className="text-white text-xs font-medium line-clamp-1 hover:text-emerald-400 transition-colors">
                  {product.name}
                </h3>
              </Link>
              <div className="flex items-center justify-between mt-1">
                <span className="text-emerald-400 font-bold text-sm">
                  {product.price?.toFixed(2)} €
                </span>
                {/* Hide quick add button for hubs and kits */}
                {product.subcategory !== 'hubs' && product.subcategory !== 'kits' && !product.name?.toLowerCase().includes('hub') && !product.name?.toLowerCase().includes('kit') ? (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleAddToCart(product);
                    }}
                    className="p-1.5 bg-emerald-500/10 hover:bg-emerald-500 rounded text-emerald-400 hover:text-white transition-colors"
                    title="Ajouter au panier"
                  >
                    <ShoppingCart className="w-3 h-3" />
                  </button>
                ) : (
                  <Link
                    to={`/product/${product.product_id}`}
                    className="p-1.5 bg-emerald-500/10 hover:bg-emerald-500 rounded text-emerald-400 hover:text-white transition-colors"
                    title="Voir le produit"
                  >
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <section className="mt-12 pt-8 border-t border-white/5" data-testid="product-recommendations">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white">{ruleInfo.title}</h2>
          <p className="text-sm text-white/50">{ruleInfo.subtitle}</p>
        </div>
        <Link 
          to={getShopLink()}
          className="text-emerald-400 hover:text-emerald-400-hover text-sm flex items-center gap-1"
        >
          Voir tout
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {recommendations.map(product => (
          <div 
            key={product.product_id}
            className="bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden hover:border-fgs-primary/50 transition-colors group"
          >
            <Link to={`/product/${product.product_id}`}>
              <div className="relative aspect-square bg-gradient-to-br from-fgs-bg to-fgs-surface p-4">
                <img
                  src={product.image_url || product.images?.black}
                  alt={product.name}
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                  loading="lazy"
                />
                {product.badge && (
                  <span className={`absolute top-2 left-2 px-2 py-0.5 rounded text-xs text-white ${
                    product.badge === 'new' ? 'bg-blue-500' :
                    product.badge === 'promo' ? 'bg-red-500' :
                    product.badge === 'bestseller' ? 'bg-orange-500' : 'bg-purple-500'
                  }`}>
                    {product.badge === 'new' ? 'Nouveau' :
                     product.badge === 'promo' ? 'Promo' :
                     product.badge === 'bestseller' ? 'Best-seller' : product.badge}
                  </span>
                )}
                {product.discount_percent && (
                  <span className="absolute top-2 right-2 px-2 py-0.5 bg-red-500 rounded text-xs text-white">
                    -{product.discount_percent}%
                  </span>
                )}
              </div>
            </Link>

            <div className="p-3">
              <Link to={`/product/${product.product_id}`}>
                <h3 className="text-white text-sm font-medium line-clamp-2 hover:text-emerald-400 transition-colors">
                  {product.name}
                </h3>
              </Link>
              
              <div className="flex items-center justify-between mt-2">
                <div>
                  <span className="text-emerald-400 font-bold">
                    {product.price?.toFixed(2)} €
                  </span>
                  {product.original_price && product.original_price > product.price && (
                    <span className="text-white/50 text-xs line-through ml-1">
                      {product.original_price.toFixed(2)} €
                    </span>
                  )}
                </div>
                {/* Hide quick add button for hubs and kits - they need options selection */}
                {product.subcategory !== 'hubs' && product.subcategory !== 'kits' && !product.name?.toLowerCase().includes('hub') && !product.name?.toLowerCase().includes('kit') ? (
                  <button
                    onClick={() => handleAddToCart(product)}
                    className="p-2 bg-emerald-500/10 hover:bg-emerald-500 rounded-lg text-emerald-400 hover:text-white transition-colors"
                    title="Ajouter au panier"
                  >
                    <ShoppingCart className="w-4 h-4" />
                  </button>
                ) : (
                  <Link
                    to={`/product/${product.product_id}`}
                    className="p-2 bg-emerald-500/10 hover:bg-emerald-500 rounded-lg text-emerald-400 hover:text-white transition-colors"
                    title="Voir le produit"
                  >
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default ProductRecommendations;
